import React, { useState, useRef, useEffect, useCallback } from 'react';
import { CircularProgress, Drawer, Tooltip } from '@mui/material';
import {
  ArrowUp,
  ArrowUpCircle,
  Menu as MenuIcon,
  Mic,
  Plus,
} from 'react-feather';
import parse, { HTMLReactParserOptions, Element } from 'html-react-parser';
import MessageBubble from './MessageBubble';
import { AIChatMessage } from '../../types/Chat';
import OraLogo from '../Global/OraLogo';
import { sendMessage } from '../../services/chatService';
import { Vignette, VignetteContent } from '../../types/Vignette';
import { Flashcard, FlashcardContent } from '../../types/Flashcard';
import { useAccess } from '../../hooks/useAccess';
import { hapticsImpactLight } from '../../utils/haptics';
import posthog from 'posthog-js';

interface ChatInterfaceProps {
  isStudy: boolean;
  activeVignette?: Vignette;
  activeVignetteContent?: VignetteContent;
  flashcard?: Flashcard;
  flashcardVariant?: FlashcardContent;
  placeholder?: string;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  placeholder = 'Ask me about medical topics...',
  isStudy,
  activeVignette,
  activeVignetteContent,
  flashcard,
  flashcardVariant,
}) => {
  const [messages, setMessages] = useState<AIChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [inputDisabled, setInputDisabled] = useState(false);
  const [contextMessageSent, setContextMessageSent] = useState(false);
  const [vignetteContextMessageSent, setVignetteContextMessageSent] =
    useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const proMessageRef = useRef<HTMLDivElement>(null);

  const { isGated } = useAccess();

  const parserOptions: HTMLReactParserOptions = {
    replace: (domNode) => {
      // Handle whitespace text nodes in tables to prevent DOM nesting warnings
      if (domNode.type === 'text' && domNode.data?.trim() === '') {
        return null;
      }

      if (
        domNode instanceof Element &&
        domNode.name === 'button' &&
        domNode.attribs?.class?.includes('article-button')
      ) {
        const articleId = domNode.attribs['data-article-id'];
        return (
          <a
            className="article-button"
            href={`/library/${articleId}`}
            data-article-id={articleId}
          >
            {domNode.children[0]?.type === 'text'
              ? domNode.children[0].data
              : ''}
          </a>
        );
      }
    },
  };

  useEffect(() => {
    handleNewChat();
  }, [
    activeVignette?.id,
    activeVignetteContent,
    flashcard?.id,
    flashcardVariant?.flashcardId,
  ]);

  const onSendMessage = async (messages: AIChatMessage[]) => {
    try {
      const { stream, relatedArticlesPromise } = await sendMessage(messages);

      return new ReadableStream({
        async start(controller) {
          let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
          let responseContent = '';

          try {
            // First, pipe through all content from the original stream
            reader = stream.getReader();

            let reading = true;
            while (reading) {
              const { done, value } = await reader.read();
              if (done) {
                reading = false;
                break;
              }

              // Accumulate content to check if it's the error message
              const text = new TextDecoder().decode(value);
              responseContent += text;

              controller.enqueue(value);
            }

            // Check if the response is the error message - if so, skip article processing
            if (
              responseContent.trim() === 'Sorry, please try a different input.'
            ) {
              // Skip article processing for error responses
            } else {
              // Then handle the articles with a timeout
              try {
                const articleTimeout = new Promise<never>((_, reject) => {
                  setTimeout(
                    () => reject(new Error('Article processing timeout')),
                    5000
                  );
                });

                const articles = (await Promise.race([
                  relatedArticlesPromise,
                  articleTimeout,
                ])) as Array<{ articleId: string; articleTitle: string }>;
                const uniqueArticles = new Set<string>();
                const uniqueArticlesList = articles.filter(
                  (article: { articleId: string; articleTitle: string }) => {
                    if (uniqueArticles.has(article.articleId)) return false;
                    uniqueArticles.add(article.articleId);
                    return true;
                  }
                );

                if (uniqueArticlesList.length > 0) {
                  const articleButtons = uniqueArticlesList
                    .map(
                      (article: { articleId: string; articleTitle: string }) =>
                        `<a class="article-button" href="/library/${article.articleId}"  data-article-id="${article.articleId}">${article.articleTitle}</a>`
                    )
                    .join('');

                  const buttonsHtml = `
                    <div class="related-articles">
                      <h4>Related Articles</h4>
                      ${articleButtons}
                    </div>
                  `;
                  controller.enqueue(new TextEncoder().encode(buttonsHtml));
                }
              } catch (articleError) {
                console.error('Error processing articles:', articleError);
                // Don't fail the entire stream if articles fail
              }
            }
          } catch (streamError) {
            console.error('Error in stream processing:', streamError);
            // Ensure we close the controller even on error
          } finally {
            // Always close the reader and controller
            if (reader) {
              try {
                reader.releaseLock();
              } catch (e) {
                console.error('Error releasing reader lock:', e);
              }
            }
            controller.close();
          }
        },
      });
    } catch (error) {
      console.error('Error in handleMessage:', error);
      return null;
    }
  };

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const resetState = useCallback(() => {
    setIsGenerating(false);
    setInputDisabled(false);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const handleSend = useCallback(async () => {
    if (isGated()) {
      return;
    }

    const trimmedInput = inputValue.trim();
    if (!trimmedInput || isGenerating) return;

    // Create abort controller for this request
    abortControllerRef.current = new AbortController();

    // Check if we need to send a context message for flashcard
    const shouldSendContextMessage =
      flashcard && flashcardVariant && !contextMessageSent;

    // Check if we need to send a context message for vignette
    const shouldSendVignetteContextMessage =
      activeVignette && activeVignetteContent && !vignetteContextMessageSent;

    const messagesToSend: AIChatMessage[] = [...messages];

    if (shouldSendContextMessage) {
      const contextMessage: AIChatMessage = {
        id: `context-${Date.now()}`,
        content: `Context: ${flashcardVariant.back}`,
        role: 'user',
      };
      messagesToSend.push(contextMessage);
      setContextMessageSent(true);
    }

    if (shouldSendVignetteContextMessage) {
      const answerOptionsText = activeVignetteContent.answerOptions
        .map((option) => `${option.optionLetter}. ${option.optionText}`)
        .join('\n');

      const vignetteContextMessage: AIChatMessage = {
        id: `vignette-context-${Date.now()}`,
        content: `Context: This is a multiple choice question with the following details:\n\nQuestion: ${activeVignetteContent.question}\n\nAvailable Answer Choices:\n${answerOptionsText}\n\nThe correct answer is: ${activeVignetteContent.correctAnswer}\n\nExplanation for the correct answer: ${activeVignetteContent.explanation}\n\nNote: When referring to answer choices, use the exact letter (A, B, C, D, E, F, etc.) as shown above.`,
        role: 'user',
      };
      messagesToSend.push(vignetteContextMessage);
      setVignetteContextMessageSent(true);
    }

    const userMessage: AIChatMessage = {
      id: `user-${Date.now()}`,
      content: trimmedInput,
      role: 'user',
    };

    const assistantMessage: AIChatMessage = {
      id: `assistant-${Date.now()}`,
      content: '',
      role: 'assistant',
      isStreaming: true,
    };

    // Update state atomically
    setMessages((prev) => [...prev, userMessage, assistantMessage]);
    setInputValue('');
    setIsGenerating(true);
    setInputDisabled(true);

    let stream: ReadableStream<Uint8Array> | null = null;
    let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;

    try {
      stream = await onSendMessage([
        ...messagesToSend,
        userMessage,
        assistantMessage,
      ]);

      if (!stream) {
        throw new Error('No stream returned');
      }

      reader = stream.getReader();
      let accumulatedContent = '';

      let reading = true;
      while (reading) {
        const { done, value } = await reader.read();
        if (done) {
          reading = false;
          break;
        }

        const text = new TextDecoder().decode(value);
        accumulatedContent += text;

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantMessage.id
              ? { ...msg, content: accumulatedContent }
              : msg
          )
        );
      }

      // Finalize the message - treat all responses as valid
      const finalContent = accumulatedContent.trim();

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === assistantMessage.id
            ? {
                ...msg,
                content: finalContent,
                isStreaming: false,
              }
            : msg
        )
      );
    } catch (error) {
      console.error('Error in chat stream:', error);

      // Only update error if it's not an abort error
      if (error instanceof Error && error.name !== 'AbortError') {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantMessage.id
              ? {
                  ...msg,
                  content: 'Error: Failed to load response. Please try again.',
                  isStreaming: false,
                }
              : msg
          )
        );
      }
    } finally {
      // Always cleanup and reset state
      if (reader) {
        try {
          reader.releaseLock();
        } catch (e) {
          console.error('Error releasing reader lock:', e);
        }
      }
      resetState();
    }
  }, [
    inputValue,
    isGenerating,
    messages,
    onSendMessage,
    resetState,
    flashcard,
    flashcardVariant,
    contextMessageSent,
    activeVignette,
    activeVignetteContent,
    vignetteContextMessageSent,
    isGated,
  ]);

  // Add a safety timeout to ensure state is reset if something goes wrong
  useEffect(() => {
    if (isGenerating) {
      const timeoutId = setTimeout(() => {
        console.warn('Chat generation timeout - resetting state');
        resetState();
      }, 30000); // 30 second timeout

      return () => clearTimeout(timeoutId);
    }
  }, [isGenerating, resetState]);

  // Force reset if state gets stuck
  useEffect(() => {
    if (inputDisabled && !isGenerating) {
      console.warn('State inconsistency detected - forcing reset');
      setTimeout(() => {
        setInputDisabled(false);
      }, 100);
    }
  }, [inputDisabled, isGenerating]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      setIsGenerating(false);
      setInputDisabled(false);
    };
  }, []);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setInputValue(e.target.value);
    },
    []
  );

  const handleNewChat = useCallback(() => {
    // Reset messages to empty array
    setMessages([]);
    // Clear any current input
    setInputValue('');
    // Reset any ongoing generation state
    setIsGenerating(false);
    setInputDisabled(false);
    // Reset context message sent state
    setContextMessageSent(false);
    setVignetteContextMessageSent(false);
    // Abort any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const canSend = !isGenerating && inputValue.trim().length > 0;

  const renderPanelContent = (hideNewChat: boolean) => {
    return (
      <div className="chat-interface_panel_history">
        {!hideNewChat && (
          <div className="chat-interface_panel_history_header">
            <button className="button button--glass" onClick={handleNewChat}>
              <Plus />
              New Chat
            </button>
          </div>
        )}
        <div className="chat-interface_panel_history_body">
          <h3>Chat History</h3>
          <p>Previous chats will appear here. This feature is coming soon.</p>
        </div>
      </div>
    );
  };

  const determineShake = () => {
    if (proMessageRef.current) {
      proMessageRef.current.classList.add('shake');
      setTimeout(() => {
        proMessageRef.current?.classList.remove('shake');
      }, 500);
    }
  };

  const renderProMessage = () => {
    return (
      <div className="pro-message" ref={proMessageRef}>
        <a
          className="button button--link"
          href="/pricing"
          onClick={() => {
            hapticsImpactLight();
            posthog?.capture('pricing_click_from_ai_chat');
          }}
        >
          <ArrowUpCircle />
          Upgrade to Pro to unlock AI Chat
        </a>
      </div>
    );
  };

  return (
    <>
      <div
        className={`chat-interface ${messages.length === 0 ? 'chat-interface--start' : ''}`}
      >
        {!isGated() && (
          <div className="chat-interface_panel">
            <div className="chat-interface_panel_header">
              <OraLogo />
              <div className="badge badge--pro">BETA</div>
            </div>
            {renderPanelContent(false)}
          </div>
        )}
        <div className="chat-interface_main">
          <div className="chat-interface_main_header">
            <button
              className="button button--icon-only"
              onClick={() => setMenuOpen(true)}
            >
              <MenuIcon />
            </button>
            <div className="chat-interface_main_header_logo">
              <OraLogo />
              <div className="badge badge--pro">BETA</div>
            </div>
            <button
              className="button button--icon-only"
              onClick={handleNewChat}
              disabled={isGated()}
            >
              <Plus />
            </button>
          </div>
          <div ref={chatContainerRef} className="chat-interface_main_messages">
            {isGated() && isStudy && renderProMessage()}
            {messages.map((message) => (
              <div
                key={message.id}
                className={`chat-interface_main_messages_message chat-interface_main_messages_message--${message.role}`}
              >
                <MessageBubble role={message.role}>
                  {message.role === 'assistant' &&
                  message.isStreaming &&
                  !message.content ? (
                    <div
                      style={{
                        color: 'var(--type-heading)',
                      }}
                    >
                      <CircularProgress size={16} />
                    </div>
                  ) : (
                    <div>{parse(message.content, parserOptions)}</div>
                  )}
                </MessageBubble>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
          <div className="chat-interface_main_input">
            <div className="start-message">
              <OraLogo />
              <h1>Ask about medical topics</h1>
            </div>
            <div className="chat-submit-container">
              <form onSubmit={handleSend} onMouseEnter={determineShake}>
                <textarea
                  ref={textareaRef}
                  style={{ maxHeight: '100px' }}
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  placeholder={placeholder}
                  disabled={inputDisabled || isGated()}
                />
                <div className="form-footer">
                  <div className="form-footer_actions">
                    <Tooltip
                      title={'Speech to text coming soon.'}
                      enterTouchDelay={0}
                    >
                      <span>
                        <button
                          className="button button--icon-only"
                          onClick={() => {}}
                          disabled
                          aria-label="Speech to text"
                        >
                          <Mic />
                        </button>
                      </span>
                    </Tooltip>
                    <button
                      className="button button--glass button--send"
                      onClick={handleSend}
                      disabled={!canSend}
                      aria-label="Send message"
                      tabIndex={0}
                      type="submit"
                    >
                      <ArrowUp />
                    </button>
                  </div>
                  <div className="disclaimer">
                    Ora AI can make mistakes. Check important info. Ora AI is
                    not a clinical tool.
                  </div>
                </div>
              </form>
            </div>
            {isGated() && !isStudy && renderProMessage()}
          </div>
        </div>
      </div>
      <Drawer
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        anchor="left"
        sx={{
          '& .MuiDrawer-paper': {
            width: '16rem',
          },
        }}
      >
        {renderPanelContent(true)}
      </Drawer>
    </>
  );
};

export default ChatInterface;
